import { useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { useInternalTelemetry } from '../useInternalTelemetry';
import { IQosContextProps, QosContext } from './QosContext';
import { IComponent } from './types';

interface IQosHookOptions {
  /**
   * If this option is true, this hook will do nothing when used without a
   * {@link IQosContextProps}. This is used by the outermost QosProvider because it doesn't have any parent that provides context.
   * Otherwise, a warning will be logged
   */
  readonly disableWithoutContext?: boolean;
  /**
   * Determine if the QosProvider is critical that contributes to the first meaningful content
   */
  readonly isCritical?: boolean;
}

export interface IUseQosReturnType {
  /**
   * Report a QoS event when the component finished loading without any failure. This method should be
   * used by those components that require remote data fetching.
   */
  reportLoadComplete: () => void;
  /**
   * Report a Qos event when the component failed to load.
   * This method should be called only when a fatal or unexpected error occurs that makes the component unusable
   */
  reportFailure: (error: any) => void;
}

/**
 * Component can use this React hook to report QoS status. The component must be a child of a QosProvider.
 * When isCritical is true, the component will become part of
 * the First Meaningful Paint (FMP).
 * @param componentName Name of the component
 * @param param1 {@link IQosHookOptions }
 * @returns callbacks to report QoS
 */
export const useQos = (
  componentName: string,
  { disableWithoutContext = false, isCritical = false }: IQosHookOptions = {}
): IUseQosReturnType => {
  const isReportPending = useRef(true);
  const qosContext = useContext(QosContext);
  const { telemetry, registerToFirstMeaningfulContent, unRegisterToFirstMeaningfulContent } = useInternalTelemetry();
  const componentRef = useRef<IComponent>({
    componentName
  });

  const qos = useMemo((): IQosContextProps | null => {
    if (qosContext) {
      return qosContext;
    }

    if (!disableWithoutContext) {
      telemetry.warn('QosProvider not found', {
        message: 'The component uses useQos hook must be wrapped by a QosProvider'
      });
    }

    return null;
  }, [qosContext, telemetry, disableWithoutContext]);

  useEffect(() => {
    componentRef.current.componentName = componentName;
  }, [componentName]);

  // When the isCritical changes, register or unregister accordingly
  useEffect(() => {
    const component = componentRef.current;
    if (isCritical) {
      registerToFirstMeaningfulContent(component);
    } else {
      unRegisterToFirstMeaningfulContent(component);
    }

    return () => {
      // Unregister when unmounts
      isCritical && unRegisterToFirstMeaningfulContent(component);
    };
  }, [isCritical, registerToFirstMeaningfulContent, unRegisterToFirstMeaningfulContent]);

  useEffect(() => {
    // Don't re-register with a new QosProvider if the component has already
    // reported a result (loadComplete or loadFailure), because the component may
    // not report again, which could cause the new QoS context to timeout.
    if (!isReportPending.current || qos === null) {
      return;
    }

    const component = componentRef.current;
    qos?.registerComponent(component);

    return () => {
      qos?.unregisterComponent(component);
    };
  }, [qos, registerToFirstMeaningfulContent, unRegisterToFirstMeaningfulContent]);

  return {
    reportLoadComplete: useCallback(() => {
      isReportPending.current = false;
      qos?.reportLoadComplete(componentRef.current);
      // Every time a critical component reports complete, check if the first meaningful paint is complete
      isCritical && unRegisterToFirstMeaningfulContent(componentRef.current);
    }, [qos, isCritical, unRegisterToFirstMeaningfulContent]),
    reportFailure: useCallback(
      (error: any) => {
        isReportPending.current = false;
        qos?.reportFailure(componentRef.current, error);
      },
      [qos]
    )
  };
};
